import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { getProblemDetail } from "service/api/content/getApi";
import { postProblem, putProblem } from "service/api/content/postApi";
import { OneBtnModal } from "components/modal";
import { HeadText } from "components/header";
import { ProblemDetailTable } from "./problemDetailTale";
import Editor from "components/editor";
import { TableInput } from "components/input";
import { DetailActionBtn } from "components/button";
import routerConstant from "service/constant/routerConstant";
import { TestcaseRow, TestcaseSheet } from "./testcaseSheet";

export interface IChangeData {
  title: string;
  category: number;
  limitSeconds: {
    C: number;
    Cpp: number;
    Java: number;
    Python: number;
  };
  limitMemory: {
    C: string;
    Cpp: string;
    Java: string;
    Python: string;
  };
  limitLoop: string;
  language: string[];
}

const Cover = styled.div``;

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 40px 0 104px;
  .title {
    margin: 0 0 15px;
    line-height: 175%;
    color: #232d42;
    font-size: 18px;
    font-weight: 500;
  }
  .margin {
    margin-top: 40px;
  }
  .container {
    min-height: 76px;
    padding: 12px 16px;
    border: 1px solid #eee;
  }
  .example {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const BtnContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
`;

export function ProbolemDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [changeData, setChangeData] = useState<IChangeData>({} as IChangeData);
  const [problemEditValue, setProblemEditValue] = useState<string>("");
  const [inputEditValue, setInputEditValue] = useState<string>("");
  const [outputEditValue, setOutputEditValue] = useState<string>("");
  const [testcases, setTestcases] = useState<TestcaseRow[]>([]);
  // const [commentValue, setCommentValue] = useState<string>("");
  const [sourceValue, setSourceValue] = useState<string>("");
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] =
    useState<boolean>(false);
  const [isErrModalVisible, setIsErrModalVisible] = useState<boolean>(false);

  useEffect(() => {
    if (params.id !== "add") {
      setIsUpdated(false);
      (async () => {
        const detailRes = await getProblemDetail(params.id + "");
        const timeLimit = detailRes.data.data.time_limit ?? {};
        const memoryLimit = detailRes.data.data.memory_limit ?? {};
        const languageLimit = detailRes.data.data.language_limit ?? {};
        setChangeData({
          title: detailRes.data.data.title_ko,
          category: detailRes.data.data.ProblemCategoryId,
          limitSeconds: {
            C: timeLimit[1] ?? timeLimit[0],
            Cpp: timeLimit[2] ?? timeLimit[0],
            Java: timeLimit[3] ?? timeLimit[0],
            Python: timeLimit[4] ?? timeLimit[0],
          },
          limitMemory: {
            C: memoryLimit[1] ?? memoryLimit[0],
            Cpp: memoryLimit[2] ?? memoryLimit[0],
            Java: memoryLimit[3] ?? memoryLimit[0],
            Python: memoryLimit[4] ?? memoryLimit[0],
          },
          limitLoop:
            detailRes.data.data.loop_limit === -1
              ? ""
              : detailRes.data.data.loop_limit,
          language: [
            languageLimit[1] ? "C" : "",
            languageLimit[2] ? "C++" : "",
            languageLimit[3] ? "Java" : "",
            languageLimit[4] ? "Python" : "",
          ].filter((language) => language !== ""),
        });
        setProblemEditValue(detailRes.data.data.description_ko);
        setInputEditValue(detailRes.data.data.input_format_ko);
        setOutputEditValue(detailRes.data.data.output_format_ko);
        setSourceValue(detailRes.data.data.authorShip);
        setTestcases(
          (detailRes.data.data.Testcases ?? [])
            .slice()
            .sort(
              (a: { order?: number; id?: number }, b: { order?: number; id?: number }) =>
                (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0),
            )
            .map((test: { id?: number; input?: string; output?: string; show?: number }) => ({
              id: test.id,
              input: test.input ?? "",
              output: test.output ?? "",
              show: test.show === 0 ? 0 : 1,
            })),
        );
        setIsUpdated(true);
      })();
    }
  }, [params]);

  const handleSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSourceValue(e.target.value);
  };

  const handleCancelBtn = () => {
    navigate(-1);
  };

  const handleSaveBtn = async () => {
    const body = {
      title_ko: changeData.title,
      is_spj: true,
      description_ko: problemEditValue,
      input_format_ko: inputEditValue,
      output_format_ko: outputEditValue,
      language_limit: {
        0: changeData.language.length === 0 ? 0 : 1,
        1: changeData.language.includes("C") ? 1 : 0,
        2: changeData.language.includes("C++") ? 1 : 0,
        3: changeData.language.includes("Java") ? 1 : 0,
        4: changeData.language.includes("Python") ? 1 : 0,
      },
      memory_limit: {
        0: changeData.limitMemory.C,
        1: changeData.limitMemory.C,
        2: changeData.limitMemory.Cpp,
        3: changeData.limitMemory.Java,
        4: changeData.limitMemory.Python,
      },
      time_limit: {
        0: Number(changeData.limitSeconds.Python),
        1: Number(changeData.limitSeconds.C),
        2: Number(changeData.limitSeconds.Cpp),
        3: Number(changeData.limitSeconds.Java),
        4: Number(changeData.limitSeconds.Python),
      },
      loop_limit: changeData.limitLoop ? Number(changeData.limitLoop) : -1,
      authorShip: sourceValue,
      ProblemCategoryId: changeData.category,
    };
    const res =
      params.id === "add" ? await postProblem(body) : await putProblem(params.id + "", body);
    if (res.status === 201 || res.status === 200) {
      setIsSuccessModalVisible(true);
    } else if (res.status === 220) {
      setIsErrModalVisible(true);
    }
  };

  const handleSuccessBtn = () => {
    navigate(routerConstant.contents.problem);
  };

  return (
    <Cover>
      <OneBtnModal
        isVisible={isSuccessModalVisible}
        setIsVisible={setIsSuccessModalVisible}
        text="저장 완료되었습니다"
        onClick={handleSuccessBtn}
      />
      <OneBtnModal
        isVisible={isErrModalVisible}
        setIsVisible={setIsErrModalVisible}
        text="권한이 없습니다"
      />
      <HeadText label="문제 상세" margin="0 0 28px" />
      <ProblemDetailTable data={changeData} setData={setChangeData} />
      <EditContainer>
        <div>
          <p className="title">문제*</p>
          {!isUpdated && (
            <Editor height="379px" setValue={setProblemEditValue} />
          )}
          {isUpdated && (
            <Editor
              height="379px"
              value={problemEditValue}
              setValue={setProblemEditValue}
            />
          )}
        </div>
        <div>
          <p className="title margin">Input*</p>
          {!isUpdated && <Editor height="379px" setValue={setInputEditValue} />}
          {isUpdated && (
            <Editor
              height="379px"
              value={inputEditValue}
              setValue={setInputEditValue}
            />
          )}
        </div>
        <div>
          <p className="title margin">Output*</p>
          {!isUpdated && (
            <Editor height="379px" setValue={setOutputEditValue} />
          )}
          {isUpdated && (
            <Editor
              height="379px"
              value={outputEditValue}
              setValue={setOutputEditValue}
            />
          )}
        </div>
        {/* <div>
          <p className="title">해설*</p>
          <Editor
            height="379px"
            value={commentValue}
            setValue={setCommentValue}
          />
        </div> */}
        <div>
          <p className="title">출처*</p>
          <TableInput value={sourceValue} onChange={handleSourceChange} />
        </div>
      </EditContainer>
      <BtnContainer>
        <DetailActionBtn
          isFill={false}
          label="목록으로"
          onClick={handleCancelBtn}
        />
        <DetailActionBtn label="저장하기" onClick={handleSaveBtn} />
      </BtnContainer>
      {params.id === "add" ? (
        <p style={{ margin: "32px 0 0", color: "#6c757d" }}>
          문제를 저장한 뒤 테스트케이스를 등록할 수 있습니다.
        </p>
      ) : (
        isUpdated && params.id && (
          <TestcaseSheet problemId={params.id} initialRows={testcases} />
        )
      )}
    </Cover>
  );
}
